import type { BreedId, UpgradeDef } from './types';
import { HEN_BREEDS, BREED_IDS } from './breeds';

/**
 * Upgrade catalog. Every effect here is applied by core/modifiers.ts and
 * described by core/describe.ts — there are no text-only upgrades.
 * tests/unit/data.test.ts fails if an upgrade has no effect.
 */

const feed: UpgradeDef[] = [
  { id: 'FEED_1', name: 'Scratch Grain', category: 'feed', cost: 390,
    requires: [{ kind: 'hens', count: 3 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.5 }],
    flavor: 'Basic grain. The hens act like it is a five-course meal.' },
  { id: 'FEED_2', name: 'Layer Pellets', category: 'feed', cost: 10_000,
    requires: [{ kind: 'hens', count: 6 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.5 }, { stat: 'lay', op: 'mul', value: 1.1 }],
    flavor: 'Balanced nutrition in a boring brown pellet.' },
  { id: 'FEED_3', name: 'Oyster Shell Supplement', category: 'feed', cost: 75_000,
    requires: [{ kind: 'hens', count: 10 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.5 }, { stat: 'salvageWindow', op: 'add', value: 15 }],
    flavor: 'Calcium for thicker shells. Even the rotten ones hold together longer.' },
  { id: 'FEED_4', name: 'Organic Feed', category: 'feed', cost: 250_000,
    requires: [{ kind: 'hens', count: 15 }, { kind: 'lifetimeEarned', amount: 10_000 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.5 }, { stat: 'value', op: 'mul', value: 1.2, scope: { kind: 'tier', tier: 'RARE' } }],
    flavor: 'Certified organic. Rare breeds are especially smug about it.' },
  { id: 'FEED_5', name: 'Premium Blend', category: 'feed', cost: 5_000_000,
    requires: [{ kind: 'hens', count: 35 }, { kind: 'lifetimeEarned', amount: 100_000 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.5 }, { stat: 'hatchReward', op: 'mul', value: 1.25 }],
    flavor: 'Hand-mixed. The chicks hatch already judging your other feed.' },
  { id: 'FEED_6', name: 'Custom Formula', category: 'feed', cost: 60_000_000,
    requires: [{ kind: 'hens', count: 50 }, { kind: 'lifetimeEarned', amount: 1_000_000 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.5 }, { stat: 'lay', op: 'mul', value: 1.2 }],
    flavor: 'Formulated by a poultry nutritionist who will not stop emailing you.' },
];

/** Breed training: ×2 laying speed per tier (design rule: training = laying speed, not value). */
const TRAINING_COSTS: Partial<Record<BreedId, number[]>> = {
  RIR: [50, 1_000, 30_000, 350_000],
  LEGHORN: [80, 1_600, 45_000, 500_000],
  BUFF_ORPINGTON: [30, 600, 18_000, 200_000],
  WYANDOTTE: [40, 800, 24_000, 260_000],
  AUSTRALORP: [400, 10_000],
  PLYMOUTH_ROCK: [500, 12_000],
  ISA_BROWN: [250, 6_000],
  WELSUMMER: [600, 15_000],
  NEW_HAMPSHIRE_RED: [400, 10_000],
  AMERAUCANA: [45_000],
  BLACK_COPPER_MARANS: [30_000],
  EASTER_EGGER: [1_200],
  CREAM_LEGBAR: [6_000],
  BARNEVELDER: [3_000],
  SILKIE: [20_000],
  OLIVE_EGGER: [16_000],
  SPECKLED_SUSSEX: [10_000],
  BIELEFELDER: [30_000],
  SVART_HONA: [200_000],
  AYAM_CEMANI: [500_000],
  POLISH: [40_000],
  FRIZZLE: [60_000],
  TURKEN: [36_000],
  SULTAN: [150_000],
  DONG_TAO: [80_000],
  BRAHMA: [70_000],
  COCHIN: [44_000],
  SERAMA: [56_000],
  JAPANESE_BANTAM: [90_000],
  SEBRIGHT: [120_000],
  MILLE_FLEUR_DUCCLE: [110_000],
};

const ROMAN = ['I', 'II', 'III', 'IV', 'V'];

export function trainingId(breed: BreedId, tier: number): string {
  return `TRAIN_${breed}_${tier}`;
}

const training: UpgradeDef[] = BREED_IDS.flatMap((breed) =>
  (TRAINING_COSTS[breed] ?? []).map((cost, i) => ({
    id: trainingId(breed, i + 1),
    name: `${HEN_BREEDS[breed].name} Training ${ROMAN[i]}`,
    category: 'training' as const,
    cost,
    requires: i === 0
      ? [{ kind: 'ownBreed' as const, breed, count: 1 }]
      : [{ kind: 'upgrade' as const, id: trainingId(breed, i) }],
    effects: [{ stat: 'lay' as const, op: 'mul' as const, value: 2, scope: { kind: 'breed' as const, breed } }],
  })),
);

const click: UpgradeDef[] = [
  { id: 'CLICK_1', name: 'Two-Handed Collection', category: 'click', cost: 100,
    requires: [{ kind: 'hens', count: 2 }], effects: [], clickGather: 2,
    flavor: 'Revolutionary. Why did nobody think of this?' },
  { id: 'CLICK_2', name: 'Egg Basket', category: 'click', cost: 800,
    requires: [{ kind: 'hens', count: 4 }], effects: [], clickGather: 6,
    flavor: 'Wicker. Classic. Holds six if you are brave.' },
  { id: 'CLICK_3', name: 'Wheelbarrow Haul', category: 'click', cost: 20_000,
    requires: [{ kind: 'hens', count: 8 }], effects: [], clickGather: 12,
    flavor: 'Structurally unsound for eggs. Works anyway.' },
  { id: 'CLICK_4', name: 'Experienced Hands', category: 'click', cost: 100_000,
    requires: [{ kind: 'hens', count: 12 }], effects: [], clickGather: Infinity,
    flavor: 'You can clear a nest without looking. The hens find it unsettling.' },
  { id: 'CLICK_5', name: 'Mechanical Egg Arm', category: 'click', cost: 2_000_000,
    requires: [{ kind: 'hens', count: 30 }, { kind: 'roosters', count: 3 }],
    effects: [{ stat: 'clickValue', op: 'mul', value: 5 }],
    flavor: 'Hydraulic. Gentle. Slightly menacing.' },
  { id: 'CLICK_6', name: 'Egg Vacuum 3000', category: 'click', cost: 50_000_000,
    requires: [{ kind: 'hens', count: 50 }, { kind: 'roosters', count: 5 }],
    effects: [{ stat: 'clickValue', op: 'mul', value: 2 }],
    flavor: 'Somehow it does not break them. Science.' },
];

const rooster: UpgradeDef[] = [
  { id: 'ROOSTER_1', name: 'Better Genetics', category: 'rooster', cost: 5_000,
    requires: [{ kind: 'roosters', count: 1 }], effects: [{ stat: 'crowCooldown', op: 'mul', value: 0.85 }] },
  { id: 'ROOSTER_2', name: 'Louder Crow', category: 'rooster', cost: 30_000,
    requires: [{ kind: 'roosters', count: 1 }, { kind: 'lifetimeEarned', amount: 500 }],
    effects: [{ stat: 'crowDuration', op: 'add', value: 30 }] },
  { id: 'ROOSTER_3', name: 'Alpha Rooster', category: 'rooster', cost: 200_000,
    requires: [{ kind: 'roosters', count: 2 }], effects: [{ stat: 'crowCooldown', op: 'mul', value: 0.8 }] },
  { id: 'ROOSTER_4', name: 'Master Crower', category: 'rooster', cost: 1_500_000,
    requires: [{ kind: 'roosters', count: 3 }], effects: [{ stat: 'crowCooldown', op: 'mul', value: 0.75 }] },
  { id: 'ROOSTER_5', name: 'Auto-Crow', category: 'rooster', cost: 20_000_000,
    requires: [{ kind: 'roosters', count: 5 }, { kind: 'lifetimeEarned', amount: 1_000_000 }],
    effects: [{ stat: 'autoCrow', op: 'add', value: 1 }] },
  { id: 'ROOSTER_6', name: 'Lightning Crow', category: 'rooster', cost: 200_000_000,
    requires: [{ kind: 'roosters', count: 7 }],
    effects: [{ stat: 'crowCooldown', op: 'mul', value: 0.65 }, { stat: 'crowPower', op: 'mul', value: 1.25 }] },
];

const golden: UpgradeDef[] = [
  { id: 'GOLDEN_1', name: "Fool's Gold", category: 'golden', cost: 1_000,
    requires: [{ kind: 'goldenClicked', count: 1 }], effects: [{ stat: 'goldenSpawn', op: 'mul', value: 1.25 }] },
  { id: 'GOLDEN_2', name: 'Silver Lining', category: 'golden', cost: 5_000,
    requires: [{ kind: 'silverClicked', count: 1 }], effects: [{ stat: 'silverWindow', op: 'add', value: 3 }] },
  { id: 'GOLDEN_3', name: 'Golden Touch', category: 'golden', cost: 25_000,
    requires: [{ kind: 'goldenClicked', count: 5 }], effects: [{ stat: 'goldenWindow', op: 'add', value: 5 }] },
  { id: 'GOLDEN_4', name: 'Storm Chaser', category: 'golden', cost: 150_000,
    requires: [{ kind: 'goldenClicked', count: 10 }], effects: [{ stat: 'silverPayout', op: 'mul', value: 1.5 }] },
  { id: 'GOLDEN_5', name: 'Lucky Clutch', category: 'golden', cost: 1_000_000,
    requires: [{ kind: 'lifetimeEarned', amount: 100_000 }], effects: [{ stat: 'clutchPower', op: 'mul', value: 2 }] },
  { id: 'GOLDEN_6', name: 'Perpetual Shimmer', category: 'golden', cost: 10_000_000,
    requires: [{ kind: 'rareBreedsOwned', count: 3 }], effects: [{ stat: 'goldenSpawn', op: 'mul', value: 1.5 }] },
];

const wrangler: UpgradeDef[] = [
  { id: 'WRANGLER_1', name: 'Egg Picker', category: 'wrangler', cost: 1_000,
    requires: [{ kind: 'achievements', count: 10 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.1 }, { stat: 'goldenWindow', op: 'add', value: 3 }] },
  { id: 'WRANGLER_2', name: 'Hen Helper', category: 'wrangler', cost: 50_000,
    requires: [{ kind: 'achievements', count: 25 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.1 }, { stat: 'silverSpawn', op: 'mul', value: 1.2 }] },
  { id: 'WRANGLER_3', name: 'Coop Keeper', category: 'wrangler', cost: 2_500_000,
    requires: [{ kind: 'achievements', count: 40 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.1 }, { stat: 'gatherRate', op: 'mul', value: 1.15 }] },
  { id: 'WRANGLER_4', name: 'Flock Manager', category: 'wrangler', cost: 50_000_000,
    requires: [{ kind: 'achievements', count: 55 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.1 }, { stat: 'eventEffect', op: 'mul', value: 1.25 }] },
  { id: 'WRANGLER_5', name: 'Poultry Magnate', category: 'wrangler', cost: 500_000_000,
    requires: [{ kind: 'achievements', count: 70 }],
    effects: [{ stat: 'value', op: 'mul', value: 1.1 }, { stat: 'offlineCapHours', op: 'add', value: 2 }] },
];

/** Coop upgrades are bought per flock; effects apply only to that flock. Cost × flock tax. */
const coop: UpgradeDef[] = [
  { id: 'COOP_1', name: 'Flock Watchtower', category: 'coop', cost: 20_000,
    requires: [{ kind: 'hens', count: 4 }], effects: [{ stat: 'value', op: 'mul', value: 1.15 }] },
  { id: 'COOP_2', name: 'Heated Floor', category: 'coop', cost: 100_000,
    requires: [{ kind: 'hens', count: 8 }], effects: [{ stat: 'value', op: 'mul', value: 1.2 }] },
  { id: 'COOP_3', name: 'Predator-Proof Fencing', category: 'coop', cost: 400_000,
    requires: [{ kind: 'hens', count: 15 }], effects: [{ stat: 'value', op: 'mul', value: 1.3 }] },
  { id: 'COOP_4', name: 'LED Lighting System', category: 'coop', cost: 5_000_000,
    requires: [{ kind: 'hens', count: 25 }], effects: [{ stat: 'lay', op: 'mul', value: 1.25 }, { stat: 'value', op: 'mul', value: 1.2 }] },
  { id: 'COOP_5', name: 'Automatic Feeder System', category: 'coop', cost: 60_000_000,
    requires: [{ kind: 'hens', count: 40 }], effects: [{ stat: 'value', op: 'mul', value: 2 }] },
];

export const UPGRADES: readonly UpgradeDef[] = [...feed, ...training, ...click, ...rooster, ...golden, ...wrangler, ...coop];

export const UPGRADES_BY_ID: ReadonlyMap<string, UpgradeDef> = new Map(UPGRADES.map((u) => [u.id, u]));

export const UPGRADE_CATEGORY_LABELS: Record<UpgradeDef['category'], string> = {
  feed: 'Feed Quality',
  training: 'Breed Training',
  click: 'Collection',
  rooster: 'Rooster',
  golden: 'Golden & Silver Eggs',
  wrangler: 'Hen Wrangler',
  coop: 'Coop (this flock)',
};
